// src/services/userService.js
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

/**
 * ユーザープロフィールを取得する
 * @param {string} userId - ユーザーID
 * @returns {Promise<Object|null>} ユーザープロフィール
 */
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('ユーザープロフィールの取得エラー:', error);
    throw new Error('ユーザープロフィールの取得に失敗しました。');
  }
};

/**
 * ユーザープロフィールを作成する
 * @param {string} userId - ユーザーID
 * @param {string} email - メールアドレス
 * @param {string} displayName - 表示名
 * @returns {Promise<Object>} 作成されたユーザープロフィール
 */
export const createUserProfile = async (userId, email, displayName) => {
  try {
    const userProfile = {
      email: email,
      username: displayName || email.split('@')[0],
      displayName: displayName || email.split('@')[0],
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      subscription: {
        planId: 'free',
        status: 'active',
        startDate: new Date().toISOString(),
        endDate: null
      },
      role: 'user',
      trizLawsMastery: {},
      growthPatternProfile: null,
      teamMemberships: [],
      onboardingCompleted: false,
      settings: {
        notifications: true,
        language: 'ja',
        theme: 'light'
      }
    };

    await setDoc(doc(db, 'users', userId), userProfile);
    return { id: userId, ...userProfile };
  } catch (error) {
    console.error('ユーザープロフィールの作成エラー:', error);
    throw new Error('ユーザープロフィールの作成に失敗しました。');
  }
};

/**
 * ユーザープロフィールを更新する
 * @param {string} userId - ユーザーID
 * @param {Object} updates - 更新データ
 * @returns {Promise<void>}
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('ユーザープロフィールの更新エラー:', error);
    throw new Error('ユーザープロフィールの更新に失敗しました。');
  }
};

/**
 * 全ユーザーを取得する（管理者用）
 * @returns {Promise<Array>} ユーザー一覧
 */
export const getAllUsers = async () => {
  try {
    const usersCollection = collection(db, 'users');
    const usersSnapshot = await getDocs(usersCollection);
    return usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('ユーザー一覧の取得エラー:', error);
    throw new Error('ユーザー一覧の取得に失敗しました。');
  }
};

/**
 * ユーザーのサブスクリプション状態を更新する
 * @param {string} userId - ユーザーID
 * @param {Object} subscriptionData - サブスクリプションデータ
 * @returns {Promise<void>}
 */
export const updateUserSubscription = async (userId, subscriptionData) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      subscription: subscriptionData,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('サブスクリプションの更新エラー:', error);
    throw new Error('サブスクリプションの更新に失敗しました。');
  }
};

/**
 * ユーザーの最終ログイン時刻を更新する
 * @param {string} userId - ユーザーID
 * @returns {Promise<void>}
 */
export const updateLastLogin = async (userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      lastLogin: new Date().toISOString()
    });
  } catch (error) {
    console.error('最終ログイン時刻の更新エラー:', error);
    // ログインは成功させたいので、ここではエラーを投げない
  }
};

/**
 * ユーザーの診断結果を保存する
 * @param {string} userId - ユーザーID
 * @param {string} diagnosisType - 診断タイプ
 * @param {Object} results - 診断結果
 * @returns {Promise<void>}
 */
export const saveDiagnosisResults = async (userId, diagnosisType, results) => {
  try {
    const updateData = {
      [`${diagnosisType}Results`]: results,
      [`last${diagnosisType.charAt(0).toUpperCase() + diagnosisType.slice(1)}Date`]: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(doc(db, 'users', userId), updateData);
  } catch (error) {
    console.error('診断結果の保存エラー:', error);
    throw new Error('診断結果の保存に失敗しました。');
  }
};